import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RequireAccess } from '../components/RequireAccess';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { CountrySelect } from '../components/CountrySelect';
import { FlagIcon } from '../components/FlagIcon';
import { countryName } from '../lib/countries';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import {
  fetchCulturalEvents,
  createCulturalEvent,
  updateCulturalEvent,
  deleteCulturalEvent,
  type CulturalEventInput,
} from '../lib/queries';
import type { CulturalEvent, CulturalEventCategory } from '../lib/types';
import './Calendar.css';

/**
 * /calendar — an editorial almanac of EXTERNAL cultural moments (Met Gala,
 * Fashion Week, Earth Day, World Mental Health Day, …). NOT club events or
 * auctions — it reads exclusively from public.cultural_events.
 *
 * Desktop = 7-column month grid; mobile (<= 640px) = per-day list. Events
 * are colour-coded by category, filterable via the header toggle row, and
 * open a detail popover on click. Admins (role admin/developer) get an
 * "+ Add event" button plus edit/delete from the detail view.
 */

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_HEADING_FMT = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  year: 'numeric',
});
const DAY_HEADING_FMT = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  day: 'numeric',
});
const DAY_WEEKDAY_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
});
const FULL_DATE_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

// ---------- Category metadata --------------------------------------------

interface CategoryMeta {
  key: CulturalEventCategory;
  label: string;
  /** Short glyph carried on the chip. Brand-minimal, not a rainbow. */
  glyph: string;
}

const CATEGORIES: CategoryMeta[] = [
  { key: 'vintage_fairs', label: 'Vintage Fairs', glyph: '▲' },
  { key: 'fashion', label: 'Fashion', glyph: '◆' },
  { key: 'sustainability', label: 'Sustainability', glyph: '✦' },
];

const CATEGORY_LABEL: Record<CulturalEventCategory, string> = CATEGORIES.reduce(
  (acc, c) => {
    acc[c.key] = c.label;
    return acc;
  },
  {} as Record<CulturalEventCategory, string>,
);
const CATEGORY_GLYPH: Record<CulturalEventCategory, string> = CATEGORIES.reduce(
  (acc, c) => {
    acc[c.key] = c.glyph;
    return acc;
  },
  {} as Record<CulturalEventCategory, string>,
);

// ---------- Date helpers (cultural_events are 'YYYY-MM-DD' local) ----------

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Parse a 'YYYY-MM-DD' string into a local Date at midnight. */
function parseDateOnly(s: string): Date {
  const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Format a Date back to 'YYYY-MM-DD' (for <input type="date"> values). */
function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Human label for an event's date or date range. */
function eventDateLabel(ev: CulturalEvent): string {
  const start = FULL_DATE_FMT.format(parseDateOnly(ev.event_date));
  if (!ev.end_date || ev.end_date === ev.event_date) return start;
  return `${start} – ${FULL_DATE_FMT.format(parseDateOnly(ev.end_date))}`;
}

// ---------- Location helpers ----------------------------------------------
// A location tag = (country code, city). Either can be set alone; an event
// with neither simply has no location and never appears in the filter.

interface LocationOption {
  key: string;             // stable id for the <option> + filter compare
  country: string | null;
  city: string | null;
  label: string;           // human text, e.g. "London, United Kingdom"
}

/** True when an event carries any location tag. */
function hasLocation(ev: CulturalEvent): boolean {
  return !!(ev.country || ev.city?.trim());
}

/** Stable key for a (country, city) pair so identical tags dedupe. */
function locationKey(country: string | null, city: string | null): string {
  return `${(country ?? '').toUpperCase()}|${(city ?? '').trim().toLowerCase()}`;
}

/** Text label for a location tag: "City, Country" / "City" / "Country". */
function locationLabel(country: string | null, city: string | null): string {
  const c = city?.trim();
  const name = country ? countryName(country) : '';
  return [c, name].filter(Boolean).join(', ');
}

/** Distinct, sorted locations across the events the team has tagged. */
function deriveLocations(events: CulturalEvent[]): LocationOption[] {
  const byKey = new Map<string, LocationOption>();
  for (const ev of events) {
    if (!hasLocation(ev)) continue;
    const key = locationKey(ev.country, ev.city);
    if (byKey.has(key)) continue;
    byKey.set(key, {
      key,
      country: ev.country,
      city: ev.city?.trim() || null,
      label: locationLabel(ev.country, ev.city),
    });
  }
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
}

interface MonthCell {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  isPast: boolean;
  events: CulturalEvent[];
}

/**
 * Build the 6×7 grid. Multi-day events land on EVERY day in their range
 * that falls within the grid window (start..end inclusive).
 */
function buildMonthCells(
  year: number,
  monthIndex: number,
  events: CulturalEvent[],
): MonthCell[] {
  const today = startOfDay(new Date());
  const firstOfMonth = new Date(year, monthIndex, 1);
  const leading = firstOfMonth.getDay(); // Sunday = 0
  const gridStart = new Date(year, monthIndex, 1 - leading);

  // Bucket events by every day they occupy.
  const byDay = new Map<string, CulturalEvent[]>();
  for (const ev of events) {
    const start = startOfDay(parseDateOnly(ev.event_date));
    const end = ev.end_date ? startOfDay(parseDateOnly(ev.end_date)) : start;
    const cur = new Date(start);
    // Guard against runaway ranges; a cultural moment won't span >366 days.
    let guard = 0;
    while (cur <= end && guard < 400) {
      const key = dayKey(cur);
      const arr = byDay.get(key) ?? [];
      arr.push(ev);
      byDay.set(key, arr);
      cur.setDate(cur.getDate() + 1);
      guard += 1;
    }
  }

  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const dayEvents = (byDay.get(dayKey(d)) ?? [])
      .slice()
      .sort((a, b) => a.event_date.localeCompare(b.event_date) || a.title.localeCompare(b.title));
    cells.push({
      date: d,
      inMonth: d.getMonth() === monthIndex,
      isToday: sameDay(d, today),
      isPast: d < today && !sameDay(d, today),
      events: dayEvents,
    });
  }
  return cells;
}

// ---------- Chip ----------------------------------------------------------

function CalendarChip({
  event,
  onDate,
  onClick,
}: {
  event: CulturalEvent;
  /** The grid cell's date — used to detect a continuing multi-day run. */
  onDate: Date;
  onClick: () => void;
}) {
  const isMultiDay = !!event.end_date && event.end_date !== event.event_date;
  const isStart = sameDay(parseDateOnly(event.event_date), onDate);
  const glyph = CATEGORY_GLYPH[event.category];
  return (
    <button
      type="button"
      className={`calendar__chip calendar__chip--${event.category}`}
      onClick={onClick}
      title={`${CATEGORY_LABEL[event.category]} · ${event.title}`}
    >
      <span className="calendar__chip-glyph" aria-hidden="true">
        {isMultiDay && !isStart ? '·' : glyph}
      </span>
      <span className="calendar__chip-title">{event.title}</span>
      {isMultiDay && (
        <span className="calendar__chip-range" aria-hidden="true">
          {isStart ? '→' : '·'}
        </span>
      )}
    </button>
  );
}

// ---------- Month cell ----------------------------------------------------

function MonthCellView({
  cell,
  onChipClick,
}: {
  cell: MonthCell;
  onChipClick: (ev: CulturalEvent) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const MAX_CHIPS = 3;
  const overflow = cell.events.length - MAX_CHIPS;
  const visible = expanded ? cell.events : cell.events.slice(0, MAX_CHIPS);

  const cls = [
    'calendar__cell',
    !cell.inMonth && 'calendar__cell--outside',
    cell.inMonth && cell.isPast && 'calendar__cell--past',
    cell.isToday && 'calendar__cell--today',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls}>
      <div className="calendar__cell-head">
        <span className="calendar__cell-num">{cell.date.getDate()}</span>
      </div>
      {visible.length > 0 && (
        <div className="calendar__cell-chips">
          {visible.map((ev) => (
            <CalendarChip
              key={ev.id}
              event={ev}
              onDate={cell.date}
              onClick={() => onChipClick(ev)}
            />
          ))}
        </div>
      )}
      {overflow > 0 && !expanded && (
        <button
          type="button"
          className="calendar__cell-more"
          onClick={() => setExpanded(true)}
        >
          +{overflow} more
        </button>
      )}
    </div>
  );
}

// ---------- Detail modal --------------------------------------------------

function EventDetail({
  event,
  isAdmin,
  onEdit,
  onDelete,
  onClose,
}: {
  event: CulturalEvent;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onBackdrop = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <div
      ref={overlayRef}
      className="calendar-modal"
      role="dialog"
      aria-modal="true"
      aria-label={event.title}
      onClick={onBackdrop}
    >
      <div className="calendar-modal__panel">
        <button
          ref={closeRef}
          type="button"
          className="calendar-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <span className={`calendar__badge calendar__badge--${event.category}`}>
          <span aria-hidden="true">{CATEGORY_GLYPH[event.category]}</span>
          {CATEGORY_LABEL[event.category]}
        </span>

        <h2 className="calendar-modal__title">{event.title}</h2>
        <p className="calendar-modal__date">{eventDateLabel(event)}</p>

        {hasLocation(event) && (
          <p className="calendar-modal__location">
            <FlagIcon code={event.country} />
            {locationLabel(event.country, event.city)}
          </p>
        )}

        {!event.is_published && (
          <p className="calendar-modal__draft">Draft, hidden from members.</p>
        )}

        {event.description && (
          <p className="calendar-modal__body">{event.description}</p>
        )}

        <div className="calendar-modal__actions">
          {event.external_url && (
            <a
              className="btn btn--primary btn--sm"
              href={event.external_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Official source ↗
            </a>
          )}
          {isAdmin && (
            <>
              <button type="button" className="btn btn--ghost btn--sm" onClick={onEdit}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm calendar-modal__delete"
                onClick={onDelete}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Add / edit form modal -----------------------------------------

function EventForm({
  initial,
  busy,
  onSubmit,
  onClose,
}: {
  /** Existing event when editing; null when creating. */
  initial: CulturalEvent | null;
  busy: boolean;
  onSubmit: (input: CulturalEventInput) => void;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [eventDate, setEventDate] = useState(
    initial?.event_date ?? toDateInput(new Date()),
  );
  const [endDate, setEndDate] = useState(initial?.end_date ?? '');
  const [category, setCategory] = useState<CulturalEventCategory>(
    initial?.category ?? 'fashion',
  );
  const [externalUrl, setExternalUrl] = useState(initial?.external_url ?? '');
  const [isPublished, setIsPublished] = useState(initial?.is_published ?? true);
  const [country, setCountry] = useState(initial?.country ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      setErr('A title is required.');
      return;
    }
    if (!eventDate) {
      setErr('A date is required.');
      return;
    }
    if (endDate && endDate < eventDate) {
      setErr('End date must be on or after the start date.');
      return;
    }
    setErr(null);
    onSubmit({
      title: t,
      description: description.trim() || null,
      event_date: eventDate,
      end_date: endDate || null,
      category,
      external_url: externalUrl.trim() || null,
      is_published: isPublished,
      country: country || null,
      city: city.trim() || null,
    });
  }

  return (
    <div
      ref={overlayRef}
      className="calendar-modal"
      role="dialog"
      aria-modal="true"
      aria-label={initial ? 'Edit cultural event' : 'Add cultural event'}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="calendar-modal__panel">
        <button
          type="button"
          className="calendar-modal__close"
          onClick={onClose}
          aria-label="Close"
          disabled={busy}
        >
          ×
        </button>

        <h2 className="calendar-modal__title">
          {initial ? 'Edit event' : 'Add cultural event'}
        </h2>

        <form className="calendar-form" onSubmit={submit}>
          <label className="calendar-form__field">
            <span className="calendar-form__label">Title</span>
            <input
              className="calendar-form__input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Met Gala"
              maxLength={160}
              autoFocus
            />
          </label>

          <label className="calendar-form__field">
            <span className="calendar-form__label">Description</span>
            <textarea
              className="calendar-form__input calendar-form__textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is it and why does it matter?"
              rows={3}
            />
          </label>

          <div className="calendar-form__row">
            <label className="calendar-form__field">
              <span className="calendar-form__label">Date</span>
              <input
                className="calendar-form__input"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
              />
            </label>
            <label className="calendar-form__field">
              <span className="calendar-form__label">End date (optional)</span>
              <input
                className="calendar-form__input"
                type="date"
                value={endDate}
                min={eventDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>

          <label className="calendar-form__field">
            <span className="calendar-form__label">Category</span>
            <select
              className="calendar-form__input"
              value={category}
              onChange={(e) => setCategory(e.target.value as CulturalEventCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <div className="calendar-form__row">
            <div className="calendar-form__field">
              <span className="calendar-form__label">Country (optional)</span>
              <CountrySelect
                value={country}
                onChange={setCountry}
                className="calendar-form__input"
                ariaLabel="Event country"
              />
            </div>
            <label className="calendar-form__field">
              <span className="calendar-form__label">City (optional)</span>
              <input
                className="calendar-form__input"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. London"
                maxLength={80}
              />
            </label>
          </div>

          <label className="calendar-form__field">
            <span className="calendar-form__label">Official link (optional)</span>
            <input
              className="calendar-form__input"
              type="url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://…"
            />
          </label>

          <label className="calendar-form__check">
            <input
              type="checkbox"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
            />
            <span>Published (visible to members)</span>
          </label>

          {err && <p className="calendar-form__error">{err}</p>}

          <div className="calendar-modal__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
              {busy ? 'Saving…' : initial ? 'Save changes' : 'Add event'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Page ----------------------------------------------------------

type FormState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; event: CulturalEvent };

function CalendarInner() {
  const { profile } = useSession();
  const toast = useToast();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'developer';

  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState(today.getMonth());
  const [events, setEvents] = useState<CulturalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [active, setActiveFilters] = useState<Set<CulturalEventCategory>>(
    () => new Set(CATEGORIES.map((c) => c.key)),
  );
  // '' = all locations; otherwise a locationKey() the team has tagged events with.
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [selected, setSelected] = useState<CulturalEvent | null>(null);
  const [form, setForm] = useState<FormState>({ mode: 'closed' });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(
    async (signal?: AbortSignal) => {
      // Pad the range by a week each side so multi-day events that begin in
      // the trailing/leading grid days still resolve.
      const rangeStart = toDateInput(new Date(year, monthIndex, 1 - 7));
      const rangeEnd = toDateInput(new Date(year, monthIndex + 1, 7));
      const data = await fetchCulturalEvents(rangeStart, rangeEnd, signal);
      if (signal?.aborted) return;
      setEvents(data);
    },
    [year, monthIndex],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        await reload(ctrl.signal);
      } catch (err: unknown) {
        if (ctrl.signal.aborted) return;
        const e = err as { name?: string; message?: string };
        if (e?.name === 'AbortError') return;
        console.error('[calendar] fetch failed', err);
        setError(e?.message ?? 'Something broke loading the calendar.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [reload]);

  // Locations the team has actually tagged events with, in the loaded window.
  const locationOptions = useMemo(() => deriveLocations(events), [events]);

  // Navigating to a month without the chosen location would leave the <select>
  // on a value it no longer lists — reset to "All locations" so it stays honest.
  useEffect(() => {
    if (locationFilter && !locationOptions.some((o) => o.key === locationFilter)) {
      setLocationFilter('');
    }
  }, [locationOptions, locationFilter]);

  const filtered = useMemo(
    () =>
      events.filter(
        (e) =>
          active.has(e.category) &&
          (!locationFilter || locationKey(e.country, e.city) === locationFilter),
      ),
    [events, active, locationFilter],
  );

  const cells = useMemo(
    () => buildMonthCells(year, monthIndex, filtered),
    [year, monthIndex, filtered],
  );

  const listDays = useMemo(
    () => cells.filter((c) => c.inMonth && c.events.length > 0),
    [cells],
  );

  const hasAnyInMonth = useMemo(
    () => cells.some((c) => c.inMonth && c.events.length > 0),
    [cells],
  );

  function goPrev() {
    if (monthIndex === 0) {
      setMonthIndex(11);
      setYear((y) => y - 1);
    } else {
      setMonthIndex((m) => m - 1);
    }
  }
  function goNext() {
    if (monthIndex === 11) {
      setMonthIndex(0);
      setYear((y) => y + 1);
    } else {
      setMonthIndex((m) => m + 1);
    }
  }
  function goToday() {
    setYear(today.getFullYear());
    setMonthIndex(today.getMonth());
  }

  function toggleFilter(key: CulturalEventCategory) {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleSubmit(input: CulturalEventInput) {
    setBusy(true);
    try {
      if (form.mode === 'edit') {
        await updateCulturalEvent(form.event.id, input);
        toast.success('Event updated.');
      } else {
        await createCulturalEvent(input);
        toast.success('Event added.');
      }
      setForm({ mode: 'closed' });
      setSelected(null);
      await reload();
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e?.message ?? 'Could not save the event.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(ev: CulturalEvent) {
    if (!window.confirm(`Delete "${ev.title}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await deleteCulturalEvent(ev.id);
      toast.success('Event deleted.');
      setSelected(null);
      await reload();
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e?.message ?? 'Could not delete the event.');
    } finally {
      setBusy(false);
    }
  }

  const monthLabel = MONTH_HEADING_FMT.format(new Date(year, monthIndex, 1));
  const isCurrentMonth =
    year === today.getFullYear() && monthIndex === today.getMonth();

  return (
    <div className="calendar">
      <header className="calendar__header">
        <div className="container">
          <p className="kicker">Calendar</p>
          <div className="calendar__title-row">
            <h1 className="calendar__headline">{monthLabel}</h1>
            {isAdmin && (
              <button
                type="button"
                className="btn btn--primary btn--sm calendar__add"
                onClick={() => setForm({ mode: 'create' })}
              >
                + Add event
              </button>
            )}
          </div>

          <div className="calendar__controls" role="group" aria-label="Month navigation">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={goPrev}
              aria-label="Previous month"
            >
              ← Prev
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={goToday}
              disabled={isCurrentMonth}
            >
              Today
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={goNext}
              aria-label="Next month"
            >
              Next →
            </button>
          </div>

          <div className="calendar__filters" role="group" aria-label="Filter by category">
            {CATEGORIES.map((c) => {
              const on = active.has(c.key);
              return (
                <button
                  key={c.key}
                  type="button"
                  className={`calendar__filter calendar__filter--${c.key} ${on ? 'is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleFilter(c.key)}
                >
                  <span className="calendar__filter-glyph" aria-hidden="true">
                    {c.glyph}
                  </span>
                  {c.label}
                </button>
              );
            })}
          </div>

          {locationOptions.length > 0 && (
            <div className="calendar__location">
              <label className="calendar__location-label" htmlFor="calendar-location">
                Location:
              </label>
              <div className="calendar__location-select">
                {locationFilter && (
                  <span className="calendar__location-flag" aria-hidden="true">
                    <FlagIcon
                      code={
                        locationOptions.find((o) => o.key === locationFilter)?.country ?? null
                      }
                    />
                  </span>
                )}
                <select
                  id="calendar-location"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All locations</option>
                  {locationOptions.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <DotRule className="calendar__rule" />
        </div>
      </header>

      <div className="container">
        {loading ? (
          <div className="calendar__skeleton" aria-busy="true" aria-label="Loading calendar">
            <Skeleton variant="card" aspectRatio={16 / 10} />
          </div>
        ) : error ? (
          <EmptyState
            eyebrow="Error"
            headline="We couldn't load the calendar."
            body={error}
          />
        ) : (
          <>
            {/* Desktop month grid */}
            <div className="calendar__grid-wrap">
              <div className="calendar__weekdays" role="row">
                {DAY_LABELS.map((d) => (
                  <div key={d} className="calendar__weekday" role="columnheader">
                    {d}
                  </div>
                ))}
              </div>
              <div className="calendar__grid" role="grid" aria-label={`${monthLabel} calendar`}>
                {cells.map((cell, i) => (
                  <MonthCellView key={i} cell={cell} onChipClick={setSelected} />
                ))}
              </div>
              {!hasAnyInMonth && (
                <p className="calendar__empty">
                  No cultural events this month
                  {active.size < CATEGORIES.length || locationFilter
                    ? ' for the current filters'
                    : ''}
                  .
                </p>
              )}
            </div>

            {/* Mobile per-day list */}
            <div className="calendar__list">
              {listDays.length === 0 ? (
                <EmptyState
                  eyebrow="Nothing this month"
                  headline="No cultural events this month."
                  body="Check back as the team curates upcoming moments."
                />
              ) : (
                listDays.map((cell) => {
                  const dateLabel = DAY_HEADING_FMT.format(cell.date).toUpperCase();
                  const weekday = DAY_WEEKDAY_FMT.format(cell.date);
                  return (
                    <section
                      key={cell.date.toISOString()}
                      className={`calendar__day ${cell.isPast ? 'calendar__day--past' : ''} ${cell.isToday ? 'calendar__day--today' : ''}`}
                    >
                      <header className="calendar__day-head">
                        <span className="calendar__day-date">{dateLabel}</span>
                        <span className="calendar__day-sep" aria-hidden="true">·</span>
                        <span className="calendar__day-weekday">{weekday}</span>
                      </header>
                      <ul className="calendar__day-list">
                        {cell.events.map((ev) => (
                          <li key={ev.id}>
                            <CalendarChip
                              event={ev}
                              onDate={cell.date}
                              onClick={() => setSelected(ev)}
                            />
                          </li>
                        ))}
                      </ul>
                    </section>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {selected && (
        <EventDetail
          event={selected}
          isAdmin={isAdmin}
          onEdit={() => setForm({ mode: 'edit', event: selected })}
          onDelete={() => handleDelete(selected)}
          onClose={() => setSelected(null)}
        />
      )}

      {form.mode !== 'closed' && (
        <EventForm
          initial={form.mode === 'edit' ? form.event : null}
          busy={busy}
          onSubmit={handleSubmit}
          onClose={() => setForm({ mode: 'closed' })}
        />
      )}
    </div>
  );
}

export default function Calendar() {
  return (
    <RequireAccess>
      <CalendarInner />
    </RequireAccess>
  );
}
